import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { downloadFile, registerExternal, isVideoKind } from "@/lib/resources";
import { ResourceThumb, fileTypeMeta } from "@/components/resources/ResourceThumb";
import { fmtDate } from "@/lib/credits";
import { Download, Eye, Heart, Star, Play, Clock } from "lucide-react";

export function ResourceCard({ resource, onFavoriteChange, showFavorite = true }) {
  const navigate = useNavigate();
  const { label } = fileTypeMeta(resource.file_type, resource.kind);
  const video = isVideoKind(resource.kind);
  const canDownload = (resource.has_file || resource.external_url) && (resource.allow_download || video || resource.kind === "external");

  const doDownload = async (e) => {
    e.stopPropagation();
    if (resource.kind === "youtube") { navigate(`/resources/${resource.id}`); return; }
    const isExternal = resource.external_url && (!resource.has_file || ["external", "youtube", "vimeo"].includes(resource.kind));
    try {
      if (isExternal) {
        const url = await registerExternal(resource.id);
        if (url) window.open(url, "_blank", "noopener");
      } else {
        await downloadFile(resource.id, resource.original_filename);
        toast.success("Download started");
      }
    } catch (err) { toast.error(err.detail || formatApiError(err.response?.data?.detail) || "Download unavailable"); }
  };

  const toggleFav = async (e) => {
    e.stopPropagation();
    try {
      const { data } = await api.post(`/resources/${resource.id}/favorite`);
      onFavoriteChange && onFavoriteChange(resource.id, data.favorite);
      toast.success(data.favorite ? "Added to favorites" : "Removed from favorites");
    } catch (err) { toast.error("Failed to update favorite"); }
  };

  return (
    <div
      onClick={() => navigate(`/resources/${resource.id}`)}
      className="group flex cursor-pointer flex-col overflow-hidden rounded-2xl border border-white/[0.06] bg-[#141414] transition-all duration-300 hover:-translate-y-1 hover:border-[#E30613]/40 hover:shadow-[0_12px_40px_-12px_rgba(227,6,19,0.35)]"
      data-testid={`resource-card-${resource.id}`}
    >
      <div className="relative aspect-[16/10] overflow-hidden">
        <ResourceThumb resource={resource} className="transition-transform duration-500 group-hover:scale-105" />
        <div className="absolute left-3 top-3 flex gap-2">
          <span className="rounded-full border border-white/10 bg-black/60 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white backdrop-blur">{resource.category}</span>
        </div>
        {resource.featured && (
          <span className="absolute right-3 top-3 flex items-center gap-1 rounded-full bg-[#E30613] px-2 py-0.5 text-[10px] font-bold uppercase text-white"><Star className="h-3 w-3 fill-white" /> Featured</span>
        )}
        {video && (
          <div className="absolute inset-0 flex items-center justify-center opacity-0 transition-opacity group-hover:opacity-100">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[#E30613]/90"><Play className="ml-0.5 h-5 w-5 fill-white text-white" /></span>
          </div>
        )}
      </div>

      <div className="flex flex-1 flex-col p-4">
        <div className="mb-1 flex items-center justify-between text-[11px] text-neutral-500">
          <span className="font-medium text-neutral-400">{label} · v{resource.version}</span>
          <span className="flex items-center gap-1" title={resource.month_downloads !== undefined ? "Downloads this month" : "Total downloads"}><Download className="h-3 w-3" /> {resource.month_downloads !== undefined ? `${resource.month_downloads} this month` : resource.download_count}</span>
        </div>
        <h3 className="line-clamp-1 font-display text-base font-semibold text-white">{resource.title}</h3>
        <p className="mt-1 line-clamp-2 flex-1 text-xs leading-relaxed text-neutral-500">{resource.description || "—"}</p>
        <div className="mt-3 flex items-center gap-2">
          <button onClick={(e) => { e.stopPropagation(); navigate(`/resources/${resource.id}`); }} className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-white/[0.1] px-3 py-2 text-xs font-medium text-neutral-200 hover:bg-white/[0.05]" data-testid={`view-${resource.id}`}><Eye className="h-3.5 w-3.5" /> View</button>
          {canDownload && (
            <button onClick={doDownload} className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-[#E30613] px-3 py-2 text-xs font-medium text-white hover:bg-[#FF1C2A]" data-testid={`download-${resource.id}`}>
              {video && resource.kind !== "video_file" ? <><Play className="h-3.5 w-3.5" /> Watch</> : <><Download className="h-3.5 w-3.5" /> Download</>}
            </button>
          )}
          {showFavorite && (
            <button onClick={toggleFav} className={`inline-flex items-center justify-center rounded-lg border px-3 py-2 ${resource.is_favorite ? "border-[#E30613]/40 text-[#FF6069]" : "border-white/[0.1] text-neutral-400 hover:bg-white/[0.05]"}`} data-testid={`favorite-${resource.id}`}>
              <Heart className={`h-3.5 w-3.5 ${resource.is_favorite ? "fill-[#FF6069]" : ""}`} />
            </button>
          )}
        </div>
        <div className="mt-3 flex items-center gap-1 text-[10px] text-neutral-600"><Clock className="h-3 w-3" /> {fmtDate(resource.published_at || resource.created_at)}</div>
      </div>
    </div>
  );
}
